import type { ChaoServiceDef } from "@/services/chaoDeObra";
import type { EapCatalogSection } from "./eapCatalogTree";

export type ServiceStatus = "nao_iniciado" | "atencao" | "em_andamento" | "concluido";

export const CATEGORY_THEMES = [
  { bar: "bg-emerald-500", light: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", num: "bg-emerald-100 text-emerald-800" },
  { bar: "bg-blue-500", light: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200", num: "bg-blue-100 text-blue-800" },
  { bar: "bg-amber-500", light: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", num: "bg-amber-100 text-amber-800" },
  { bar: "bg-violet-500", light: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", num: "bg-violet-100 text-violet-800" },
  { bar: "bg-rose-500", light: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200", num: "bg-rose-100 text-rose-800" },
  { bar: "bg-cyan-500", light: "bg-cyan-50", text: "text-cyan-700", ring: "ring-cyan-200", num: "bg-cyan-100 text-cyan-800" },
  { bar: "bg-indigo-500", light: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200", num: "bg-indigo-100 text-indigo-800" },
] as const;

export function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export function serviceLabel(name: string): string {
  return name.includes(" — ") ? name.split(" — ").slice(1).join(" — ") : name;
}

export function groupCode(group: ChaoServiceDef): string {
  if (group.code) return group.code.padStart(2, "0").slice(0, 2);
  const m = group.name.match(/^(\d+)/);
  return m ? m[1].padStart(2, "0") : "—";
}

export function groupTitle(group: ChaoServiceDef): string {
  const parts = group.name.split(" — ");
  return (parts.length > 1 ? parts[1] : group.name).toUpperCase();
}

export function sectionLeaves(section: EapCatalogSection): ChaoServiceDef[] {
  return [...section.items, ...section.subgroups.flatMap((sg) => sg.items)];
}

export function getProgressWeight(s: ChaoServiceDef): number {
  const w = Number(s.progressWeight);
  if (!Number.isNaN(w) && w >= 0) return w;
  return 1;
}

export function weightedPct(leaves: ChaoServiceDef[], pctMap: Record<string, number>): number {
  let sumW = 0;
  let sum = 0;
  for (const l of leaves) {
    const w = getProgressWeight(l);
    if (w <= 0) continue;
    sumW += w;
    sum += w * Math.min(100, Math.max(0, pctMap[l.id] ?? 0));
  }
  if (sumW <= 0) {
    if (leaves.length === 0) return 0;
    const avg = leaves.reduce((s, l) => s + (pctMap[l.id] ?? 0), 0) / leaves.length;
    return Math.round(avg);
  }
  return Math.round((sum / sumW) * 10) / 10;
}

export function getServiceStatus(pct: number): ServiceStatus {
  if (pct >= 100) return "concluido";
  if (pct <= 0) return "nao_iniciado";
  if (pct < 30) return "atencao";
  return "em_andamento";
}

export const STATUS_LABELS: Record<ServiceStatus, string> = {
  nao_iniciado: "Não iniciado",
  atencao: "Atenção",
  em_andamento: "Em andamento",
  concluido: "Concluído",
};

export function formatLastUpdate(iso: string | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  const time = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Hoje, ${time}`;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}
